import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { UserCog, Plus, Pencil, Trash2, ShieldCheck, HeartHandshake, Stethoscope, Mail, CheckCircle2, XCircle, Camera, X, Cross, Apple, Activity, Search, Send } from "lucide-react";
import { useApp } from "../context/AppContext";
import { maskCPF } from "../utils/masks";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { MODULOS, DEFAULT_PERMS, PERFIS_CONFIGURAVEIS, temPermsCustom } from "../utils/permissoes";
import Profissionais from "./Profissionais";

const perfis = [
  { value: "administrador", label: "Administrador", Icon: ShieldCheck, desc: "Acesso total ao sistema", cls: "text-blue-700 bg-blue-50" },
  { value: "enfermeiro", label: "Enfermeiro(a)", Icon: Stethoscope, desc: "Residentes, consumo, prontuário e Receituário", cls: "text-rose-700 bg-rose-50" },
  { value: "cuidador", label: "Cuidador(a) de Idosos", Icon: HeartHandshake, desc: "Residentes, consumo, fornecimento e prontuário", cls: "text-emerald-700 bg-emerald-50" },
  { value: "gerente", label: "Gerente", Icon: ShieldCheck, desc: "Acesso configurável ao Financeiro e valores", cls: "text-indigo-700 bg-indigo-50" },
  { value: "tecnico_enfermagem", label: "Téc./Aux. de Enfermagem", Icon: Stethoscope, desc: "Residentes, consumo, fornecimento e prontuário", cls: "text-violet-700 bg-violet-50" },
  { value: "medico", label: "Médico(a)", Icon: Cross, desc: "Acesso clínico; edita anotações (não apaga)", cls: "text-sky-700 bg-sky-50" },
  { value: "nutricionista", label: "Nutricionista", Icon: Apple, desc: "Acesso clínico; edita anotações (não apaga)", cls: "text-amber-700 bg-amber-50" },
  { value: "fisioterapeuta", label: "Fisioterapeuta", Icon: Activity, desc: "Acesso clínico; edita anotações (não apaga)", cls: "text-teal-700 bg-teal-50" },
  { value: "familia", label: "Família", Icon: HeartHandshake, desc: "Portal da Família: acesso somente-leitura aos residentes vinculados", cls: "text-cyan-700 bg-cyan-50" },
];
const perfilInfo = (p) => perfis.find((x) => x.value === p) || perfis[1];
const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const BuscaUsuario = ({ lista, busca, setBusca }) => {
  const [aberto, setAberto] = useState(false);
  const sugestoes = useMemo(() => {
    const q = norm(busca.trim());
    if (!q) return [];
    return lista.filter((u) => norm(u.nome).includes(q)).slice(0, 8);
  }, [lista, busca]);
  return (
    <div className="relative w-full sm:w-72" data-testid="busca-usuario-box">
      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input className={`${inputCls} pl-9 pr-8 py-2`} placeholder="Buscar por nome..." value={busca} autoComplete="off"
        onChange={(e) => { setBusca(e.target.value); setAberto(true); }} onFocus={() => setAberto(true)} onBlur={() => setTimeout(() => setAberto(false), 150)}
        data-testid="busca-usuario" />
      {busca && <button type="button" onClick={() => setBusca("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="busca-usuario-limpar"><X className="w-4 h-4" /></button>}
      {aberto && sugestoes.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto text-sm" data-testid="busca-usuario-sugestoes">
          {sugestoes.map((u) => (
            <li key={u.id}><button type="button" className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between gap-2" onMouseDown={(e) => e.preventDefault()} onClick={() => { setBusca(u.nome); setAberto(false); }} data-testid={`busca-usuario-op-${u.id}`}>
              <span className="text-slate-700 truncate">{u.nome}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap shrink-0 ${perfilInfo(u.perfil).cls}`}>{perfilInfo(u.perfil).label}</span>
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Usuarios = () => {
  const [aba, setAba] = useState(new URLSearchParams(window.location.search).get("aba") === "profissionais" ? "profissionais" : "usuarios");
  const { getUsuarios, createUsuario, updateUsuario, deleteUsuario, residentes } = useApp();
  const { toast } = useToast();
  const [lista, setLista] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", perfil: "cuidador", funcao: "", coren: "", cpf: "", dataNascimento: "", telefone: "", foto: "", residentesIds: [], ativo: true, acessoFinanceiro: false, verValores: false, permissoes: {} });
  const [filtroPerfil, setFiltroPerfil] = useState("todos");
  const [busca, setBusca] = useState("");
  const [enviandoAcesso, setEnviandoAcesso] = useState(null);
  const fileRef = useRef(null);

  const enviarAcesso = async (u) => {
    let email = "";
    if (!window.confirm(`Enviar por e-mail os dados de acesso ao Portal da Família para ${u.nome}?`)) return;
    setEnviandoAcesso(u.id);
    try {
      let { data } = await axios.post(`${API}/usuarios/${u.id}/enviar-acesso`, { email }).catch(async (e) => {
        if (e?.response?.status === 400 && /Informe um e-mail/.test(e.response.data?.detail || "")) {
          email = window.prompt("Este familiar não tem e-mail na ficha. Informe o e-mail de destino:") || "";
          if (!email) throw e;
          return axios.post(`${API}/usuarios/${u.id}/enviar-acesso`, { email });
        }
        throw e;
      });
      if (data.ok) { toast({ title: "Dados de acesso enviados", description: `Para ${data.para}` }); carregar(); }
      else toast({ title: "Não enviado", description: data.erro || data.status });
    } catch (e) {
      toast({ title: "Erro", description: e?.response?.data?.detail || "Não foi possível enviar." });
    } finally { setEnviandoAcesso(null); }
  };

  const listaFiltrada = useMemo(() => {
    const q = norm(busca.trim());
    return lista.filter((u) => (filtroPerfil === "todos" || (filtroPerfil === "familia" ? u.perfil === "familia" : u.perfil !== "familia")) && (!q || norm(u.nome).includes(q)));
  }, [lista, filtroPerfil, busca]);

  const carregar = async () => setLista(await getUsuarios());
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const abrirNovo = () => { setEditing(null); setForm({ nome: "", email: "", senha: "", perfil: "cuidador", funcao: "", coren: "", cpf: "", dataNascimento: "", telefone: "", foto: "", residentesIds: [], ativo: true, acessoFinanceiro: false, verValores: false, permissoes: {} }); setOpen(true); };
  const abrirEdit = (u) => { setEditing(u); setForm({ nome: u.nome, email: u.email, senha: "", perfil: u.perfil, funcao: u.funcao || "", coren: u.coren || "", cpf: u.cpf || "", dataNascimento: u.dataNascimento || "", telefone: u.telefone || "", foto: u.foto || "", residentesIds: u.residentesIds || [], ativo: u.ativo, acessoFinanceiro: !!u.acessoFinanceiro, verValores: !!u.verValores, permissoes: u.permissoes || {} }); setOpen(true); };

  const configuravel = PERFIS_CONFIGURAVEIS.includes(form.perfil);
  const custom = temPermsCustom(form.permissoes);
  const setPerm = (mod, acao, val) => setForm((f) => {
    const p = { ...(f.permissoes || {}) };
    p[mod] = { ...(p[mod] || {}) };
    p[mod][acao] = val;
    if (val && acao !== "ver") p[mod].ver = true;
    if (acao === "ver" && !val) p[mod] = { ver: false, criar: false, editar: false, excluir: false };
    return { ...f, permissoes: p };
  });
  const togglePersonalizar = (on) => setForm((f) => ({ ...f, permissoes: on ? JSON.parse(JSON.stringify(DEFAULT_PERMS[f.perfil] || {})) : {} }));

  const handleFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, foto: reader.result }));
    reader.readAsDataURL(file);
  };

  // Login = CPF sem pontos; senha inicial = 6 primeiros dígitos (só ao criar; admin pode alterar)
  const handleCpf = (v) => {
    const masked = maskCPF(v);
    const digitos = masked.replace(/\D/g, "");
    setForm((f) => {
      if (editing) return { ...f, cpf: masked };
      return { ...f, cpf: masked, email: digitos, senha: digitos.slice(0, 6) };
    });
  };

  const salvar = async () => {
    if (!form.nome || !form.email) { toast({ title: "Preencha o nome e o CPF do usuário" }); return; }
    const payload = { ...form, coren: form.perfil === "cuidador" ? "" : form.coren };
    try {
      if (editing) await updateUsuario(editing.id, { nome: payload.nome, perfil: payload.perfil, funcao: payload.funcao, coren: payload.coren, cpf: payload.cpf, dataNascimento: payload.dataNascimento, telefone: payload.telefone, foto: payload.foto, residentesIds: payload.residentesIds || [], ativo: payload.ativo, acessoFinanceiro: payload.acessoFinanceiro, verValores: payload.verValores, permissoes: payload.permissoes || {}, senha: payload.senha || undefined });
      else await createUsuario(payload);
      toast({ title: editing ? "Usuário atualizado" : "Usuário cadastrado" });
      setOpen(false); carregar();
    } catch (e) {
      toast({ title: "Erro", description: e?.response?.data?.detail || "Não foi possível salvar." });
    }
  };

  const remover = async (u) => { await deleteUsuario(u.id); carregar(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Usuários & Profissionais</h1>
          <p className="text-slate-400 text-sm mt-0.5">Cadastre a equipe, os profissionais/prescritores e defina o nível de acesso de cada um</p>
        </div>
        {aba === "usuarios" && <Button onClick={abrirNovo} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="novo-usuario-btn"><Plus className="w-4 h-4" /> Novo Usuário</Button>}
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1" data-testid="usuarios-abas">
        <button onClick={() => setAba("usuarios")} className={`px-4 py-1.5 text-sm font-medium rounded-md ${aba === "usuarios" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="aba-usuarios">Usuários</button>
        <button onClick={() => setAba("profissionais")} className={`px-4 py-1.5 text-sm font-medium rounded-md ${aba === "profissionais" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="aba-profissionais">Profissionais / Médicos</button>
      </div>

      {aba === "profissionais" && <Profissionais embutido />}
      {aba === "usuarios" && (<>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 flex-wrap">
          <span className="text-sm font-medium text-slate-500">Filtrar por perfil:</span>
          <button onClick={() => setFiltroPerfil("todos")} data-testid="filtro-perfil-todos" className={`text-xs font-medium px-3 py-1.5 rounded-lg border ${filtroPerfil === "todos" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>Todos</button>
          <button onClick={() => setFiltroPerfil("familia")} data-testid="filtro-perfil-familia" className={`text-xs font-medium px-3 py-1.5 rounded-lg border ${filtroPerfil === "familia" ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-200"}`}>Família</button>
          <button onClick={() => setFiltroPerfil("equipe")} data-testid="filtro-perfil-equipe" className={`text-xs font-medium px-3 py-1.5 rounded-lg border ${filtroPerfil === "equipe" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-200"}`}>Equipe</button>
          <div className="sm:ml-auto w-full sm:w-auto"><BuscaUsuario lista={lista} busca={busca} setBusca={setBusca} /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">Nome</th>
                <th className="px-5 py-3 font-medium">E-mail / Login</th>
                <th className="px-5 py-3 font-medium">Perfil</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {listaFiltrada.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400" data-testid="usuarios-vazio">Nenhum usuário encontrado{busca ? ` para "${busca}"` : ""}.</td></tr>
              )}
              {listaFiltrada.map((u) => {
                const pi = perfilInfo(u.perfil);
                return (
                  <tr key={u.id} className="hover:bg-slate-50/60" data-testid={`usuario-row-${u.id}`}>
                    <td className="px-5 py-3.5 font-medium text-slate-700 flex items-center gap-2">
                      {u.foto ? <img src={u.foto} alt={u.nome} className="w-8 h-8 rounded-lg object-cover" />
                        : <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500">
                        {u.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                      </div>}
                      {u.nome}
                    </td>
                    <td className="px-5 py-3.5 text-slate-500">
                      <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {u.email}</span>
                      {u.perfil === "familia" && <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`acesso-enviado-${u.id}`}>{u.acessoEnviadoEm ? `Acesso enviado em ${u.acessoEnviadoEm.replace("T", " ").slice(0, 16)}` : "Acesso ainda não enviado por e-mail"}</div>}
                    </td>
                    <td className="px-5 py-3.5"><span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md ${pi.cls}`}><pi.Icon className="w-3.5 h-3.5" /> {pi.label}</span></td>
                    <td className="px-5 py-3.5">
                      {u.ativo ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Ativo</span>
                        : <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400"><XCircle className="w-3.5 h-3.5" /> Inativo</span>}
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      {u.perfil === "familia" && (
                        <button onClick={() => enviarAcesso(u)} disabled={enviandoAcesso === u.id} title="Enviar dados de acesso ao Portal da Família por e-mail" className="text-slate-400 hover:text-cyan-600 p-1.5 disabled:opacity-50" data-testid={`enviar-acesso-${u.id}`}><Send className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => abrirEdit(u)} className="text-slate-400 hover:text-blue-600 p-1.5" data-testid={`editar-usuario-${u.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remover(u)} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`excluir-usuario-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      </>)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Usuário" : "Novo Usuário"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                {form.foto ? <img src={form.foto} alt="foto" className="w-full h-full object-cover" data-testid="usuario-foto-preview" />
                  : <UserCog className="w-6 h-6 text-slate-300" />}
                {form.foto && (
                  <button type="button" onClick={() => setForm({ ...form, foto: "" })} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} data-testid="usuario-foto-input" />
                <Button type="button" variant="outline" className="gap-2" onClick={() => fileRef.current?.click()} data-testid="usuario-foto-btn"><Camera className="w-4 h-4" /> Enviar foto</Button>
                <p className="text-xs text-slate-400 mt-1">Foto do usuário (opcional)</p>
              </div>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Nome</label>
              <input className={inputCls} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">CPF</label>
                <input className={inputCls} data-testid="usuario-cpf" placeholder="000.000.000-00" value={form.cpf} onChange={(e) => handleCpf(e.target.value)} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Data de nascimento</label>
                <input type="date" className={inputCls} data-testid="usuario-nascimento" value={form.dataNascimento} onChange={(e) => setForm({ ...form, dataNascimento: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">WhatsApp / Telefone</label>
                <input className={inputCls} data-testid="usuario-telefone" placeholder="(11) 99999-9999" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Login {editing ? "" : "(CPF sem pontos)"}</label>
                <input type="text" className={inputCls} data-testid="usuario-email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">{editing ? "Nova senha (opcional)" : "Senha (6 primeiros dígitos do CPF)"}</label>
              <input type="text" className={inputCls} data-testid="usuario-senha" value={form.senha} placeholder={editing ? "Deixe em branco para manter" : ""} onChange={(e) => setForm({ ...form, senha: e.target.value })} />
              {!editing && <p className="text-xs text-slate-400 mt-1">Preenchidos automaticamente pelo CPF — você pode alterar antes de salvar.</p>}</div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Perfil de acesso</label>
              <Select value={form.perfil} onValueChange={(v) => setForm({ ...form, perfil: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{perfis.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.perfil === "familia" && (
              <div className="rounded-lg border border-cyan-200 bg-cyan-50/40 p-3" data-testid="familia-residentes" data-count={(residentes || []).length}>
                <p className="text-sm font-medium text-slate-700 mb-1">Residentes vinculados a este familiar</p>
                <p className="text-xs text-slate-500 mb-2">Marque de qual(is) residente(s) este familiar faz parte. Vários familiares podem ser vinculados ao mesmo residente. (O responsável financeiro pelo CPF já é vinculado automaticamente.)</p>
                <div className="max-h-44 overflow-y-auto grid sm:grid-cols-2 gap-1">
                  {(residentes || []).filter((r) => r.status !== "Inativo").sort((a, b) => a.nome.localeCompare(b.nome)).map((r) => {
                    const on = (form.residentesIds || []).includes(r.id);
                    return (
                      <label key={r.id} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-md cursor-pointer ${on ? "bg-cyan-100 text-cyan-900" : "hover:bg-white text-slate-700"}`}>
                        <input type="checkbox" checked={on} data-testid={`familia-res-${r.id}`}
                          onChange={(e) => setForm({ ...form, residentesIds: e.target.checked ? [...(form.residentesIds || []), r.id] : (form.residentesIds || []).filter((x) => x !== r.id) })} />
                        <span className="truncate">{r.nome}</span>{r.quarto && <span className="text-[11px] text-slate-400 ml-auto whitespace-nowrap">{r.quarto}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Função</label>
                <input className={inputCls} data-testid="usuario-funcao" placeholder="ex.: Técnica de Enfermagem" value={form.funcao} onChange={(e) => setForm({ ...form, funcao: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Registro (COREN)</label>
                <input className={inputCls} data-testid="usuario-coren" placeholder={form.perfil === "cuidador" ? "Não se aplica" : "ex.: COREN-SP 123456"} disabled={form.perfil === "cuidador"} value={form.perfil === "cuidador" ? "" : form.coren} onChange={(e) => setForm({ ...form, coren: e.target.value })} /></div>
            </div>
            {form.perfil === "gerente" && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 space-y-2" data-testid="gerente-permissoes">
                <p className="text-sm font-semibold text-indigo-800">Permissões do Gerente</p>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.acessoFinanceiro} onChange={(e) => setForm({ ...form, acessoFinanceiro: e.target.checked })} data-testid="toggle-acesso-financeiro" />
                  Acesso ao módulo Financeiro (Fornecedores, Contas a Pagar, Faturas, Relatórios/DRE)
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.verValores} onChange={(e) => setForm({ ...form, verValores: e.target.checked })} data-testid="toggle-ver-valores" />
                  Ver valores de produtos e serviços
                </label>
              </div>
            )}
            {configuravel && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="perms-matrix">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <input type="checkbox" checked={custom} onChange={(e) => togglePersonalizar(e.target.checked)} data-testid="toggle-personalizar-permissoes" />
                  Personalizar acesso por módulo (Ver / Cadastrar / Alterar / Excluir)
                </label>
                {!custom && <p className="text-xs text-slate-400">Usando o acesso padrão do perfil <b>{perfilInfo(form.perfil).label}</b>. Marque acima para liberar/restringir cada módulo do menu.</p>}
                {custom && (
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-400 text-left border-b border-slate-100">
                          <th className="py-1.5 px-1 font-medium">Módulo do menu</th>
                          <th className="py-1.5 px-1 font-medium text-center">Ver</th>
                          <th className="py-1.5 px-1 font-medium text-center">Cadastrar</th>
                          <th className="py-1.5 px-1 font-medium text-center">Alterar</th>
                          <th className="py-1.5 px-1 font-medium text-center">Excluir</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {MODULOS.map((m, idx) => (
                          <React.Fragment key={m.key}>
                          {(idx === 0 || MODULOS[idx - 1].grupo !== m.grupo) && (
                            <tr key={`g-${m.grupo}`}><td colSpan={5} className="pt-3 pb-1 px-1 text-[11px] font-bold uppercase tracking-wide text-blue-700">{m.grupo}</td></tr>
                          )}
                          <tr data-testid={`perm-row-${m.key}`}>
                            <td className="py-1.5 px-1 text-slate-600">{m.label}</td>
                            {["ver", "criar", "editar", "excluir"].map((a) => (
                              <td key={a} className="py-1.5 px-1 text-center">
                                {m.acoes.includes(a)
                                  ? <input type="checkbox" data-testid={`perm-${m.key}-${a}`} checked={!!form.permissoes?.[m.key]?.[a]} onChange={(e) => setPerm(m.key, a, e.target.checked)} className="cursor-pointer" />
                                  : <span className="text-slate-200">—</span>}
                              </td>
                            ))}
                          </tr>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[11px] text-slate-400 mt-2">Marcar Cadastrar/Alterar/Excluir libera automaticamente o "Ver". Desmarcar "Ver" oculta o módulo do menu.</p>
                  </div>
                )}
              </div>
            )}
            {editing && (
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Status</label>
                <Select value={form.ativo ? "1" : "0"} onValueChange={(v) => setForm({ ...form, ativo: v === "1" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1">Ativo</SelectItem><SelectItem value="0">Inativo</SelectItem></SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Usuarios;
